import type { AppContribution } from "@/composition";
import { type Component, createMemo, For, Show } from "solid-js";
import { Link, useLocation, useNavigate } from "@tanstack/solid-router";
import { cn } from "@gymlog/shared";
import {
  Bug,
  ChartColumn,
  ChevronsUpDown,
  Dumbbell,
  Flag,
  History,
  LayoutDashboard,
  Library,
  ListChecks,
  LogOut,
  Mail,
  Palette,
  Plug,
  Ruler,
  Scan,
  Shield,
  ShieldAlert,
  Type,
  User,
  UserCircle,
  Users,
  UsersRound,
} from "@fleet/icons";
import type { MessageDescriptor } from "@lingui/core";

import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@fleet/ui";
import { Logo } from "@/components/ui/logo";
import { SidebarNavItem } from "@/components/ui/sidebar-nav-item";
import { safeRedirectTarget, useSession } from "@/lib/auth";
import { readActiveAccountSync } from "@/lib/accounts";
import { useI18n } from "@/providers/i18n-provider";
import { useUserPreferences } from "@/providers/user-preferences-context";
import { useSignOut } from "@/providers/boot-manager";
import { useKernel } from "@fleet/kernel";
import { msg } from "@lingui/core/macro";

type NavIcon = Component<{ class?: string }>;

const inactiveClass =
  "text-muted-foreground/80 hover:bg-accent/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:bg-accent/5 transition-[background-color,color,box-shadow]";

interface AppSidebarProps {
  onCloseSidebar?: () => void;
}

export function AppSidebar(props: AppSidebarProps) {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { preferences } = useUserPreferences();
  const { promptSignOut } = useSignOut();
  const kernelApp = useKernel<AppContribution>();

  const contributedNav = createMemo(() =>
    kernelApp()
      .contributions.filter(
        (c): c is Extract<import("@/composition").AppContribution, { kind: "nav" }> =>
          c.kind === "nav",
      )
      .map((c) => c.item)
      .sort((a, b) => a.order - b.order),
  );

  // URL-addressable switcher (#15b pattern): `?account=switch` opens it. The
  // overlay itself is mounted once at the layout level (AccountSwitcherHost);
  // this trigger only flips the URL flag.
  const setSwitcherOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => {
        const next = { ...(prev as Record<string, unknown>) };
        if (open) {
          next.account = "switch";
        } else {
          delete next.account;
        }
        return next;
      },
      replace: true,
    });
  };

  // Reactive view of the active account for the switcher trigger label. Reading
  // `session.data` registers the dependency so this recomputes when the session
  // changes (switch/sign-in re-primes it).
  const activeAccount = createMemo(() => {
    void session.data;
    return readActiveAccountSync();
  });

  const activeLabel = () => {
    const a = activeAccount();
    if (a?.kind === "real") {
      return a.displayName;
    }
    return t(msg`Guest`);
  };

  // The ACTIVE account's photo comes from the local row (reactive and offline);
  // the account registry is only the bootstrap value, because it hydrates from
  // `get-session` and is not refreshed on every write.
  const activeImage = () => preferences()?.image ?? null;

  const GYM_VIEWS: { to: string; label: MessageDescriptor; icon: NavIcon }[] = [
    { to: "/", label: msg`Train`, icon: Dumbbell },
    { to: "/routines", label: msg`Routines`, icon: ListChecks },
    { to: "/exercises", label: msg`Exercises`, icon: Library },
    { to: "/history", label: msg`History`, icon: History },
    { to: "/summary", label: msg`Weekly summary`, icon: ChartColumn },
    { to: "/measurements", label: msg`Body measurements`, icon: Ruler },
  ];

  const ADMIN_ITEMS: { to: string; label: MessageDescriptor; icon: NavIcon }[] = [
    { to: "/admin", label: msg`Overview`, icon: LayoutDashboard },
    { to: "/admin/users", label: msg`Users`, icon: UsersRound },
    { to: "/admin/metrics", label: msg`Metrics`, icon: ChartColumn },
    { to: "/admin/flags", label: msg`Feature flags`, icon: Flag },
    { to: "/admin/integrations", label: msg`Integrations`, icon: Plug },
    { to: "/organizations", label: msg`Organizations`, icon: Users },
    { to: "/invitations", label: msg`Invitations`, icon: Mail },
    { to: "/admin/storage", label: msg`Storage`, icon: Shield },
    { to: "/admin/audit", label: msg`Audit`, icon: Scan },
    { to: "/admin/themes", label: msg`Themes`, icon: Palette },
    { to: "/admin/theme-audit", label: msg`Theme Audit`, icon: ShieldAlert },
    { to: "/admin/fonts", label: msg`Fonts`, icon: Type },
  ];

  const handleSignOut = () => {
    props.onCloseSidebar?.();
    promptSignOut();
  };

  return (
    <div class="flex flex-col h-full pt-[var(--safe-area-inset-top)] pb-[var(--safe-area-inset-bottom)] pl-[var(--safe-area-inset-left)]">
      <div class="flex h-16 items-center px-6 shrink-0">
        <Link
          to="/"
          class="flex items-center transition-transform hover:scale-[1.02] active:scale-95"
          onClick={() => props.onCloseSidebar?.()}
        >
          <Logo size="sm" />
        </Link>
      </div>

      <Separator />

      <nav class="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2 scrollbar-hide">
        <For each={GYM_VIEWS}>
          {(item) => (
            <SidebarNavItem
              to={item.to}
              label={t(item.label)}
              icon={item.icon}
              exact={item.to === "/"}
              onClick={() => props.onCloseSidebar?.()}
            />
          )}
        </For>

        <For each={contributedNav()}>
          {(item) => (
            <SidebarNavItem
              to={item.to}
              label={t(item.label)}
              icon={item.icon}
              exact={item.to === "/"}
              onClick={() => props.onCloseSidebar?.()}
            />
          )}
        </For>

        <Show when={session.data?.user.role === "admin"}>
          <div class="mt-4 px-3 mb-1">
            <h3 class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {t(msg`Administration`)}
            </h3>
          </div>
          <For each={ADMIN_ITEMS}>
            {(item) => (
              <SidebarNavItem
                to={item.to}
                label={t(item.label)}
                icon={item.icon}
                onClick={() => props.onCloseSidebar?.()}
              />
            )}
          </For>
        </Show>
      </nav>

      <Separator />

      {/* Bottom actions */}
      <div class="flex flex-col gap-0.5 p-2 shrink-0">
        <SidebarNavItem
          to="/profile"
          label={t(msg`Profile`)}
          icon={User}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/debug"
          label={t(msg`Debug`)}
          icon={Bug}
          onClick={() => props.onCloseSidebar?.()}
        />

        <Show
          when={session.isAuthenticated()}
          fallback={
            <Link
              to="/login"
              search={{ redirect: safeRedirectTarget(location().pathname) }}
              onClick={() => props.onCloseSidebar?.()}
              class={cn(
                "mt-1 flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                "bg-primary/10 text-primary hover:bg-primary/20",
              )}
            >
              <User class="h-4 w-4" />
              {t(msg`Sign In`)}
            </Link>
          }
        >
          <Button
            variant="ghost"
            class={`mt-1 w-full justify-start gap-2.5 px-3 py-2.5 ${inactiveClass} hover:text-destructive`}
            onClick={handleSignOut}
          >
            <LogOut class="h-4 w-4" />
            {t(msg`Sign Out`)}
          </Button>
        </Show>

        {/* ── Account switcher (multi-account #18) ────────────────────────── */}
        <Separator class="my-1" />
        <button
          type="button"
          data-testid="account-switcher-trigger"
          onClick={() => {
            setSwitcherOpen(true);
          }}
          class={cn(
            "flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors",
            inactiveClass,
          )}
          aria-label={t(msg`Switch account`)}
        >
          <Show
            when={activeAccount()?.kind === "real" ? activeAccount() : null}
            fallback={
              <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <UserCircle class="h-4 w-4" />
              </span>
            }
          >
            {(account) => (
              <Avatar
                userId={account().userId}
                name={account().displayName}
                email={account().email}
                image={activeImage() ?? account().avatar}
                size="xs"
                class="h-7 w-7 text-xs"
                testId="sidebar-avatar"
              />
            )}
          </Show>
          <span class="min-w-0 flex-1 text-left">
            <span class="block truncate font-medium text-foreground">{activeLabel()}</span>
            <Show when={activeAccount()?.email}>
              <span class="block truncate text-xs text-muted-foreground/80">
                {activeAccount()?.email}
              </span>
            </Show>
          </span>
          <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </div>
    </div>
  );
}
